import React from "react";
import { TooltipContentModule, TooltipContext } from "./tooltip-types";
import styles from "./Tooltip.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";

type MeasureStats =
  | {
      type: "aggregation";
      currentValue: any;
      childStats: {
        min: number;
        max: number;
        avg: number;
      } | null;
      relatedMeasures: Array<{
        name: string;
        value: number;
        formattedValue: string;
      }>;
      aggregation: any;
    }
  | {
      type: "pivot";
      currentValue: any;
      pivotValue: string;
      groupBys: Array<{ attribute: string }>;
      field: string;
    };

function calculateMeasureStats(context: TooltipContext): MeasureStats | null {
  const { record, currentColumn, aggregations = [] } = context;
  const measureKey = currentColumn?.measureKey;

  if (!measureKey) return null;

  const aggregation = aggregations?.find((agg) => agg?.key === measureKey);

  // Handle non-aggregation case (pivot/group data)
  if (aggregations.length === 0 || !aggregation) {
    const currentValue = record.measures?.[measureKey];
    if (!currentValue) return null;

    return {
      type: "pivot",
      currentValue,
      pivotValue: currentColumn.pivotValue || "",
      groupBys: context.groupBys || [],
      field: currentColumn.field || "",
    };
  }

  // Get the current value
  const currentValue = record.measures?.[measureKey];

  // Calculate statistics if we have children
  const childStats = record.children?.length
    ? {
        min: Math.min(
          ...record.children.map((c: any) => c.measures?.[measureKey] ?? 0)
        ),
        max: Math.max(
          ...record.children.map((c: any) => c.measures?.[measureKey] ?? 0)
        ),
        avg:
          record.children.reduce(
            (sum: number, c: any) => sum + (c.measures?.[measureKey] ?? 0),
            0
          ) / record.children.length,
      }
    : null;

  // Get related measures (other measures for the same field)
  const relatedMeasures = aggregations
    .filter((agg) => agg.field === aggregation.field && agg.key !== measureKey)
    .map((agg) => ({
      name: agg.title || agg.field,
      value: record.measures?.[agg.key] ?? 0,
      formattedValue: String(record.measures?.[agg.key] ?? 0),
    }));

  return {
    type: "aggregation",
    currentValue,
    childStats,
    relatedMeasures,
    aggregation,
  };
}

export const MeasureTooltipModule: TooltipContentModule = {
  type: "measure",
  priority: 1,
  shouldRender: (context: TooltipContext) => {
    return context.type === "measure";
  },
  render: (context: TooltipContext) => {
    // Get the field key from the field property
    const colInfo = context.currentColumn;

    if (!colInfo) return null;
    let content = null;

    const stats = calculateMeasureStats(context);
    if (!stats) return null;

    content =
      stats.type === "pivot"
        ? renderPivotMeasure(context, stats)
        : renderAggregationMeasure(context, stats);

    return (
      <CustomTooltip content={content} placement="right" size="medium">
        {context.children}
      </CustomTooltip>
    );
  },
};

function renderAggregationMeasure(context: TooltipContext, colInfo: any) {
  if (!context.field) return null;

  return (
    <div className={styles.tooltip_container}>
      <div className={styles.tooltip_header}>Measure Information</div>

      <div className={styles.tooltip_section}>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Field</div>
          <span className={styles.tooltip_value}>{context.field.target}</span>
        </div>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Attribute</div>
          <span className={styles.tooltip_value}>
            {context.field.attribute}
          </span>
        </div>
        {colInfo?.unit && (
          <div className={styles.tooltip_row}>
            <div className={styles.tooltip_label}>Unit</div>
            <span className={styles.tooltip_value}>{colInfo.unit}</span>
          </div>
        )}
      </div>

      <div className={styles.tooltip_divider}></div>

      <div className={styles.tooltip_section}>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Display Value</div>
          <div className={styles.tooltip_description}>
            {context.formattedValue}
          </div>
        </div>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Raw Value</div>
          <div className={styles.tooltip_description}>{context.value}</div>
        </div>
      </div>
    </div>
  );
}

function renderPivotMeasure(context: TooltipContext, colInfo: any) {
  const stats = calculateMeasureStats(context);
  if (!stats) return null;

  return (
    <div className={styles.tooltip_container}>
      <div className={styles.tooltip_header}>Measure Information</div>
      <div className={styles.tooltip_section}>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Entity</div>
          <span className={styles.tooltip_value}>
            {context.record.field.target}
          </span>
        </div>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Field</div>
          <span className={styles.tooltip_value}>
            {context.record.field.attribute}
          </span>
        </div>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Display Value</div>
          <span className={styles.tooltip_value}>{context.formattedValue}</span>
        </div>
        <div className={styles.tooltip_row}>
          <div className={styles.tooltip_label}>Raw Value</div>
          <span className={styles.tooltip_value}>{context.value}</span>
        </div>
      </div>
      {stats.type === "aggregation" && stats.childStats && (
        <>
          <div className={styles.tooltip_row}>
            <div className={styles.tooltip_label}>Min</div>
            <div className={styles.tooltip_value}>{stats.childStats.min}</div>
          </div>
          <div className={styles.tooltip_row}>
            <div className={styles.tooltip_label}>Max</div>
            <div className={styles.tooltip_value}>{stats.childStats.max}</div>
          </div>
          <div className={styles.tooltip_row}>
            <div className={styles.tooltip_label}>Average</div>
            <div className={styles.tooltip_value}>{stats.childStats.avg}</div>
          </div>
        </>
      )}
    </div>
  );
}
